/**
 * Item selection component for the asset metadata upload process
 */
import { Item, ItemDetail } from '@/items/api/types';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

interface ItemSelectionProps {
  onClose: () => void;
  selectedItem: ItemDetail | undefined;
  selectedItemKey: string | undefined;
  isLoadingItems: boolean;
  isLoadingItemDetail: boolean;
  items: Item[];
  onItemSelect: (item: Item | undefined) => void;
  onNext: () => void;
}

/**
 * Renders the item selection step of the asset metadata upload process
 */
export function ItemSelection({
  onClose,
  onNext,
  selectedItem,
  selectedItemKey,
  isLoadingItems,
  isLoadingItemDetail,
  items,
  onItemSelect,
}: ItemSelectionProps) {
  const isLoading = isLoadingItems || isLoadingItemDetail;

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select an item for which you want to update asset metadata
        </Typography>
        <FormControl fullWidth>
          <InputLabel id="item-select-label">Item</InputLabel>
          <Select
            labelId="item-select-label"
            id="item-select"
            value={selectedItemKey || ''}
            label="Item"
            size={'small'}
            onChange={(e) => {
              onItemSelect(items?.find((val) => val.key === e.target.value));
            }}
            disabled={isLoadingItems}
          >
            {isLoadingItems ? (
              <MenuItem value="">
                <CircularProgress size={20} /> Loading...
              </MenuItem>
            ) : (
              items?.map((item) => (
                <MenuItem key={item.id} value={item.key}>
                  {item.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        {isLoadingItemDetail && selectedItemKey && (
          <Typography
            variant="body2"
            sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <CircularProgress size={16} /> Loading item details...
          </Typography>
        )}
        {selectedItem && selectedItem.fields.length === 0 && (
          <Alert severity={'warning'} sx={{ mt: 3 }}>
            The selected item has no configured metadata
          </Alert>
        )}
      </CardContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={
            !selectedItem || selectedItem.fields.length === 0 || isLoading
          }
        >
          Next
        </Button>
      </DialogActions>
    </Card>
  );
}
